import React, { useState, useEffect, createContext, useContext } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import NavigationHeader from "@/components/ui/navigation-header";
import { 
  Smartphone, 
  QrCode, 
  User, 
  Clock
} from "lucide-react";
import { cn } from "@/lib/utils";
import POSService, { IPOS } from "@/services/pos/posService";

/**
 * Contexto para compartir información de POS con las páginas hijas
 */
interface POSContextType {
  posList: IPOS[];
  isLoadingPOS: boolean;
  selectedPOS: IPOS | null;
  setSelectedPOS: (pos: IPOS | null) => void;
}

const POSContext = createContext<POSContextType | undefined>(undefined);

/**
 * Hook para usar el contexto de POS
 */
export const usePOSContext = () => {
  const context = useContext(POSContext);
  if (context === undefined) {
    throw new Error('usePOSContext must be used within a DeliveryLayout');
  }
  return context;
};

/**
 * Props del componente DeliveryLayout
 */
interface DeliveryLayoutProps {
  children?: React.ReactNode;
}

/**
 * Layout para las páginas del personal de entrega
 *
 * Diseño minimalista optimizado para dispositivos móviles y tablets,
 * con header simple y contenido de pantalla completa.
 * Navegación táctil optimizada para personal de entrega.
 */
const DeliveryLayout: React.FC<DeliveryLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [posList, setPOSList] = useState<IPOS[]>([]);
  const [isLoadingPOS, setIsLoadingPOS] = useState(false);
  const [selectedPOS, setSelectedPOS] = useState<IPOS | null>(null);

  // Cargar lista de POS al montar el componente
  useEffect(() => {
    const loadPOSList = async () => {
      setIsLoadingPOS(true);
      try {
        const data = await POSService.getPOSList();
        setPOSList(data);
        console.log('POS list loaded in DeliveryLayout:', data);
      } catch (error) {
        console.error('Error loading POS list in DeliveryLayout:', error);
      } finally {
        setIsLoadingPOS(false);
      }
    };

    loadPOSList();
  }, []);

  // Crear el valor del contexto
  const posContextValue: POSContextType = {
    posList,
    isLoadingPOS,
    selectedPOS,
    setSelectedPOS,
  };

  const navigationItems = [
    {
      name: 'Escanear QR',
      href: '/scan',
      icon: QrCode,
      description: 'Escanear códigos QR de entrega',
      highlight: true
    },
    {
      name: 'KDS',
      href: '/kds',
      icon: Clock,
      description: 'Pedidos en cola',
    },
    {
      name: 'Perfil',
      href: '/profile',
      icon: User,
      description: 'Configuración personal'
    },
  ];

  // Logo personalizado para delivery
  const deliveryLogo = (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Smartphone className="h-6 w-6 text-primary" />
        <span className="text-xl font-bold">Chilenito</span>
      </div>
      <Badge variant="secondary" className="ml-2">
        Delivery
      </Badge>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header Unificado */}
      <NavigationHeader
        variant="cajero"
        title="Rincón Chilenito"
        subtitle="Personal de Entrega"
        logo={deliveryLogo}
        showCart={false}
        showThemeToggle={false}
        showUserMenu={true}
        userMenuVariant="compact"
      />

      {/* Navegación simplificada */}
      <nav className="border-b bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-1 py-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Button
                  key={item.name}
                  variant={isActive ? "default" : "ghost"}
                  size="sm"
                  onClick={() => navigate(item.href)}
                  className={cn(
                    "flex items-center gap-2 transition-colors",
                    isActive && item.highlight && "bg-primary text-primary-foreground shadow-sm"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{item.name}</span>
                  {item.highlight && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      QR
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Contenido principal - pantalla completa */}
      <POSContext.Provider value={posContextValue}>
        <main className="flex-1">
          {children || <Outlet />}
        </main>
      </POSContext.Provider>
    </div>
  );
};

export default DeliveryLayout;
